#!/usr/bin/env node
/**
 * Power Automate MCP Server - Entry Point
 *
 * An MCP server that enables Claude Code to interact with
 * Microsoft Power Automate for AI-assisted flow management.
 */

import { loadConfig, getConfigPath } from "./config/loader.js";
import { createMcpServer } from "./server.js";
import { logger } from "./utils/logger.js";
import { ConfigurationError } from "./utils/errors.js";
import { runSetupWizard, isSetupNeeded } from "./setup/index.js";
import { normalizeClientId, connectClient, CLIENT_LABELS, type ClientId } from "./setup/client-config.js";
import { getInstalledVersion, runSelfUpdate, backgroundUpdateCheck } from "./utils/update-checker.js";
import { runValidate } from "./cli/validate.js";
import { runDoctor } from "./cli/doctor.js";
import { runLogin } from "./cli/login.js";
import { runEmitOrgConfig } from "./setup/org-config.js";

/**
 * Parsed CLI arguments
 */
interface CliArgs {
  setup: boolean;
  help: boolean;
  version: boolean;
  validate: boolean;
  doctor: boolean;
  fixKeychain: boolean;
  update: boolean;
  login: boolean;
  emitOrgConfig: boolean;
  http: boolean;
  port: number;
  env: string | undefined;
  config: string | undefined;
  client: string | undefined;
  npx: boolean;
  debug: boolean;
}

/**
 * Parse command line arguments
 */
function parseArgs(): CliArgs {
  const args = process.argv.slice(2);

  // Parse --port <N>
  let port = 3000;
  const portIdx = args.indexOf("--port");
  if (portIdx !== -1 && args[portIdx + 1]) {
    const parsed = parseInt(args[portIdx + 1]!, 10);
    if (!Number.isNaN(parsed) && parsed > 0 && parsed < 65536) {
      port = parsed;
    }
  }

  // Parse --env <name>
  let env: string | undefined;
  const envIdx = args.indexOf("--env");
  if (envIdx !== -1 && args[envIdx + 1] && !args[envIdx + 1]!.startsWith("-")) {
    env = args[envIdx + 1]!;
  }

  // Parse --config <path>
  let config: string | undefined;
  const configIdx = args.indexOf("--config");
  if (configIdx !== -1 && args[configIdx + 1] && !args[configIdx + 1]!.startsWith("-")) {
    config = args[configIdx + 1]!;
  }

  // Parse --client <name>
  let client: string | undefined;
  const clientIdx = args.indexOf("--client");
  if (clientIdx !== -1 && args[clientIdx + 1] && !args[clientIdx + 1]!.startsWith("-")) {
    client = args[clientIdx + 1]!;
  }

  return {
    setup: args.includes("--setup") || args.includes("-s"),
    help: args.includes("--help") || args.includes("-h"),
    version: args.includes("--version") || args.includes("-v"),
    validate: args.includes("--validate"),
    doctor: args.includes("--doctor"),
    fixKeychain: args.includes("--fix-keychain"),
    update: args.includes("--update"),
    login: args.includes("--login"),
    emitOrgConfig: args.includes("--emit-org-config"),
    http: args.includes("--http"),
    port,
    env,
    config,
    client,
    npx: args.includes("--npx"),
    debug: args.includes("--debug"),
  };
}

/**
 * Get platform-specific config path for display
 */
function getConfigPathHint(): string {
  if (process.platform === "win32") {
    return "%LOCALAPPDATA%\\powerautomate-mcp\\config.json";
  } else if (process.platform === "darwin") {
    return "~/Library/Application Support/powerautomate-mcp/config.json";
  } else {
    return "~/.config/powerautomate-mcp/config.json";
  }
}

/**
 * Print help message
 */
function printHelp(): void {
  const configPath = getConfigPathHint();
  const version = getInstalledVersion();
  console.log(`
Power Automate MCP Server v${version}

Usage: powerautomate-mcp [options]

Options:
  --setup, -s      Run the interactive setup wizard
  --login          Sign in again using your existing setup (no wizard)
  --doctor         Check your setup and print exactly what to fix, then exit
  --validate       Verify config, auth, and API connectivity then exit
  --client <name>  Wire an AI app's config to this server, then exit
                   (claude, claude-code, codex, cursor, vscode, gemini, windsurf)
  --emit-org-config  Print an org.json for IT to push machine-wide, so
                   coworkers' setup finds your app registration automatically
  --npx            With --setup/--client, configure the app to run via npx
                   (no global install needed)
  --update         Check for updates and install the latest version
  --fix-keychain   macOS: rebuild the keychain item so tool calls stop prompting
  --version, -v    Print version and exit
  --http           Start with Streamable HTTP transport (for ChatGPT / remote clients)
  --port <N>       Port for HTTP transport (default: 3000)
  --env <name>     Override the default environment (alias or GUID)
  --config <path>  Use an alternate config file
  --debug          Enable debug-level logging
  --help, -h       Show this help message

Environment Variables:
  PA_MCP_CLIENT_ID    Microsoft Entra app client ID (overrides config file)
  PA_CONFIG_PATH      Custom path to config.json
  PA_MCP_ORG_CONFIG   Explicit path to an organization org.json

Organization rollout:
  IT sets up once, runs --emit-org-config, and pushes the org.json to
  %ProgramData%\\powerautomate-mcp\\ (Windows),
  /Library/Application Support/powerautomate-mcp/ (macOS), or
  /etc/powerautomate-mcp/ (Linux). Everyone else's --setup then finds the
  app registration automatically — nothing to paste. When the org.json
  includes an environmentId (--emit-org-config adds it), the MCP server can
  even start with no per-user setup at all: the user's first in-chat
  sign_in gets them running.

First Run:
  If no configuration exists, the setup wizard will run automatically.
  You can also run it manually with --setup.

  Each tenant needs its own Microsoft Entra app registration.
  The wizard can create one via Azure CLI, or you can provide one manually.

Configuration:
  Config file: ${configPath}

For more information, see the README.md file.
`);
}

/**
 * Wire a single AI client's MCP config to this server, then exit.
 * Used by the standalone `--client <name>` flag (no auth/setup required).
 */
async function runConnectOnly(clientRaw: string, useNpx: boolean): Promise<void> {
  const id = normalizeClientId(clientRaw);
  if (!id) {
    console.error(
      `Unknown client "${clientRaw}".\n` +
      `Supported: claude, claude-code, codex, cursor, vscode, gemini, windsurf`
    );
    process.exit(1);
  }

  const result = await connectClient(id, { useNpx });
  if (result.ok) {
    const verb = result.action === "unchanged" ? "already configured" : result.action === "updated" ? "updated" : "connected";
    console.error(`✓ ${result.label} ${verb}`);
    if (result.path) console.error(`  ${result.path}`);
    if (result.backupPath) console.error(`  (existing file had invalid JSON — backed up to ${result.backupPath})`);
    console.error(`\nRestart ${CLIENT_LABELS[id]} so it loads the tools. Then run: powerautomate-mcp --doctor`);
    process.exit(0);
  }

  if (result.action === "manual" && result.manualCommand) {
    console.error(`Couldn't auto-connect ${result.label}. Run this yourself:\n  ${result.manualCommand}`);
    process.exit(1);
  }
  console.error(`Couldn't connect ${result.label}: ${result.error ?? "unknown error"}`);
  process.exit(1);
}

async function main(): Promise<void> {
  const args = parseArgs();

  // --version: print and exit
  if (args.version) {
    console.log(getInstalledVersion());
    process.exit(0);
  }

  // --help: print and exit
  if (args.help) {
    printHelp();
    process.exit(0);
  }

  // --update: check npm registry and self-update
  if (args.update) {
    await runSelfUpdate();
    return; // runSelfUpdate calls process.exit
  }

  // --debug: override log level before anything else logs
  if (args.debug) {
    logger.level = "debug";
    logger.debug("Debug logging enabled via --debug");
  }

  // --config: override config path via env var (loader.ts reads PA_CONFIG_PATH)
  if (args.config) {
    process.env["PA_CONFIG_PATH"] = args.config;
    logger.debug({ configPath: args.config }, "Using custom config path");
  }

  // --validate: verify everything and exit
  if (args.validate) {
    await runValidate();
    return; // runValidate calls process.exit
  }

  // --doctor: friendly health check and exit
  if (args.doctor) {
    await runDoctor();
    return; // runDoctor calls process.exit
  }

  // --fix-keychain: rebuild the keychain item under the current node binary
  // so reads/writes stop prompting (one authorization now, silence after).
  if (args.fixKeychain) {
    const { rebuildSecureCacheItem } = await import("./auth/token-cache.js");
    console.error("Rebuilding the token-cache keychain item under the current node binary...");
    console.error("(macOS may ask for your keychain password ONE more time — approve it.)");
    try {
      const result = await rebuildSecureCacheItem();
      if (result === "unsupported") {
        console.error("Nothing to fix: keychain prompt repair only applies to macOS.");
        process.exit(0);
      }
      if (result === "empty") {
        console.error("No cached sign-in found — nothing to rebuild. Sign in first (powerautomate-mcp --login).");
        process.exit(1);
      }
      console.error("Done. Keychain prompts should stop for this node binary.");
      console.error("If prompts return after a Node upgrade, run this command again.");
      process.exit(0);
    } catch (err) {
      console.error(`Keychain repair failed: ${err instanceof Error ? err.message : String(err)}`);
      process.exit(1);
    }
  }

  // --emit-org-config: print an org.json for IT to push machine-wide, and exit
  if (args.emitOrgConfig) {
    process.exit(runEmitOrgConfig(getConfigPath));
  }

  // --login: re-authenticate only (existing config, fresh sign-in) and exit
  if (args.login) {
    const code = await runLogin();
    // Allow async MSAL cache writes to flush before exiting.
    await new Promise((resolve) => setTimeout(resolve, 1500));
    process.exit(code);
  }

  // --client without --setup: just wire that AI app's config and exit.
  // (No auth needed — this only writes the client's MCP config file.)
  if (args.client && !args.setup) {
    await runConnectOnly(args.client, args.npx);
    return; // runConnectOnly calls process.exit
  }

  // Resolve a pre-selected client for the wizard (if --client was passed).
  // "skip" is a first-class value: connect no client, ask no menu — the
  // wizard type always supported it, but normalizeClientId (rightly) doesn't
  // know it, so it has to pass through here.
  let wizardClient: ClientId | "skip" | undefined;
  if (args.client) {
    if (args.client.toLowerCase() === "skip") {
      wizardClient = "skip";
    } else {
      const normalized = normalizeClientId(args.client);
      if (normalized) {
        wizardClient = normalized;
      } else {
        console.error(`Unknown --client "${args.client}". You'll be asked to pick one.`);
      }
    }
  }

  // Run setup if requested or needed
  if (args.setup || isSetupNeeded()) {
    const result = await runSetupWizard({ client: wizardClient, useNpx: args.npx });

    if (!result.success) {
      if (result.pending) {
        // Clean IT handoff — the wizard already printed what to do next.
        // Give stdout a beat to flush (piped/buffered output is dropped by
        // an immediate process.exit).
        await new Promise((resolve) => setTimeout(resolve, 250));
        process.exit(0);
      }
      console.error(`\nSetup failed: ${result.error}`);
      process.exit(1);
    }

    if (args.setup) {
      // Allow async MSAL cache writes to flush before exiting.
      await new Promise((resolve) => setTimeout(resolve, 1500));
      process.exit(0);
    }

    // Continue to start server after auto-setup (no stdout output - MCP uses it)
  }

  logger.info(
    { platform: process.platform, nodeVersion: process.version, version: getInstalledVersion() },
    "Starting Power Automate MCP"
  );

  // Non-blocking update check on startup (prints notice to stderr if outdated)
  backgroundUpdateCheck();

  // Load configuration
  let config;
  try {
    config = loadConfig();
  } catch (error) {
    if (error instanceof ConfigurationError) {
      logger.fatal({ error: error.message }, "Configuration error");
      console.error(`\nConfiguration Error: ${error.message}\n`);
      console.error("Run with --setup to configure, or create config manually at:");
      console.error(`  ${getConfigPathHint()}\n`);
      process.exit(1);
    }
    throw error;
  }

  // --env: override the default environment
  if (args.env) {
    config.environments.default = args.env;
    logger.info({ env: args.env }, "Default environment overridden via --env");
  }

  // Create and start server
  try {
    const { start } = await createMcpServer({
      config,
      transportMode: args.http ? "http" : "stdio",
      port: args.port,
    });
    await start();
  } catch (error) {
    logger.fatal({ error }, "Failed to start MCP server");
    throw error;
  }

  // Handle graceful shutdown
  process.on("SIGINT", () => {
    logger.info("Received SIGINT, shutting down");
    process.exit(0);
  });

  process.on("SIGTERM", () => {
    logger.info("Received SIGTERM, shutting down");
    process.exit(0);
  });
}

// Run
main().catch((error) => {
  logger.fatal({ error }, "Unhandled error");
  const message = error instanceof Error ? error.message : String(error);
  console.error("Fatal error:", message);
  process.exit(1);
});
